import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { AiOutlineArrowRight, AiOutlineLeft } from "react-icons/ai";
import { IoLocation } from "react-icons/io5";
import { ROOT_URL } from "../config";

import { Heading } from "../component/Heading";

type Entity<T = any> = { id?: number | string; attributes?: T };

type ShopAttributes = {
    name?: string;
    brand_id?: number;
    address1?: string;
    address2?: string;
};

type MenuDetailData = {
    title?: string;
    sub_title?: string;
    description?: string;
    price?: number | string;
    brands?: Entity<{ name?: string }>[] | { data?: Entity<{ name?: string }>[] };
    shops?: { data?: Entity<ShopAttributes>[] };
    photo?: {
        data?: Entity<{
            url?: string;
            formats?: { large?: { url?: string }; small?: { url?: string } };
        }>[];
    };
    start_date?: string;
    expired_date?: string;
    redeemed_date?: string;
    capacity?: number;
    cuisines?: { data?: Entity<{ name?: string }>[] };
    date_str?: string;
    terms?: string;
};

type MenusDetailProps = {
    route?: { params?: { menu?: MenuDetailData; setMenu?: boolean; id?: number | string } };
    menu?: MenuDetailData;
    setMenu?: boolean;
    id?: number | string;
    isLoggedIn?: boolean;
    onRequireLogin?: () => void;
    onOpenOrderSetForm?: (payload: { setMenu: MenuDetailData; id?: number | string }) => void;
    onOpenShopDetails?: (payload: { shop: ShopAttributes; id?: number | string }) => void;
};

type BackButtonProps = {
    color?: string;
    title?: string;
};

type MeasuredImageProps = {
    src: string;
    alt: string;
    onClick?: () => void;
};

const toAbsoluteUrl = (path?: string) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${ROOT_URL}${path}`;
};

const formatZhDate = (value?: string) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return `${d.getFullYear()}年${String(d.getMonth() + 1).padStart(2, "0")}月${String(d.getDate()).padStart(2, "0")}日`;
};

export const BackButton = ({ color = "#73531D", title }: BackButtonProps) => {
    const navigate = useNavigate();

    return (
        <button
            type="button"
            onClick={() => navigate(-1)}
            style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
        >
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <AiOutlineLeft size={20} style={{ marginLeft: 20 }} color={color} />
                {title ? <div style={{ fontWeight: "700", fontSize: 18 }}>{title}</div> : null}
            </div>
        </button>
    );
};


export default function MenusDetail({
    route,
    menu,
    setMenu,
    id,
    isLoggedIn = false,
    onRequireLogin,
    onOpenOrderSetForm,
    onOpenShopDetails,
}: MenusDetailProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { company: companyParam } = useParams();
    const state = (location.state ?? {}) as {
        menu?: MenuDetailData;
        setMenu?: boolean;
        id?: number | string;
        counting: number;
    };

    const menuDetail = route?.params?.menu ?? menu ?? state.menu ?? {};
    const isSetMenu = route?.params?.setMenu ?? setMenu ?? state.setMenu ?? false;
    const counting = route?.params?.setMenu ?? state.counting ?? 0;
    const menuId = route?.params?.id ?? id ?? state.id;
    const [imageWidth, setImageWidth] = useState(0);

        useEffect(() => {
        if (!menuId) {
            navigate(`/${companyParam}/setmenulist`, { replace: true });
        }
    }, [menuId, navigate, companyParam]);

    const brandsDataFromRelation = useMemo(() => {
        const data = menuDetail?.brands as { data?: Entity<{ name?: string }>[] } | undefined;
        return data?.data ?? [];
    }, [menuDetail?.brands]);

    const brandsData = Array.isArray(menuDetail?.brands) ? menuDetail.brands : brandsDataFromRelation;

    const shopsList = useMemo(
        () => brandsDataFromRelation.map((v) => v?.attributes?.name).filter(Boolean) as string[],
        [brandsDataFromRelation]
    );

    const brandsList = useMemo(
        () => brandsData.map((v) => v?.attributes?.name).filter(Boolean) as string[],
        [brandsData]
    );

    const hasDateRange = Boolean(menuDetail?.start_date && menuDetail?.expired_date);

    if (!menuId) {
        return null;
    }

    // Dynamic centered content based on image width
    const getCenteredContent = (): React.CSSProperties => ({
        width: imageWidth > 300 ? imageWidth : '100%',
        maxWidth: "100%",
        margin: "0 auto",
    });

    const MeasuredImage = ({ src, alt, onClick }: MeasuredImageProps) => {
        const wrapperRef = useRef<HTMLDivElement | null>(null);
        const imgRef = useRef<HTMLImageElement | null>(null);
        const [size, setSize] = useState<{ width: number; height: number } | null>(null);

        const calculateSize = useCallback(() => {
            const wrapper = wrapperRef.current;
            const img = imgRef.current;
            if (!wrapper || !img || !img.naturalWidth || !img.naturalHeight) return;

            const maxW = wrapper.clientWidth;
            const maxH = window.innerHeight * 0.9;

            const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);

            const calculatedWidth = Math.floor(img.naturalWidth * scale);
            setSize({
                width: calculatedWidth,
                height: Math.floor(img.naturalHeight * scale),
            });
            setImageWidth(calculatedWidth);
        }, []);

        useEffect(() => {
            calculateSize();

            const wrapper = wrapperRef.current;
            let observer: ResizeObserver | null = null;

            if (wrapper && typeof ResizeObserver !== "undefined") {
                observer = new ResizeObserver(() => calculateSize());
                observer.observe(wrapper);
            }

            window.addEventListener("resize", calculateSize);
            return () => {
                observer?.disconnect();
                window.removeEventListener("resize", calculateSize);
            };
        }, [calculateSize]);

        return (
            <div ref={wrapperRef} style={{ width: "100%", marginBottom: 8, display: "flex", justifyContent: "center" }}>
                <img
                    ref={imgRef}
                    src={src}
                    alt={alt}
                    onLoad={calculateSize}
                    onClick={onClick}
                    style={{
                        minWidth: 300,
                        minHeight: size ? (300 * size.height) / size.width : "auto",
                        width: size?.width ?? "auto",
                        height: size?.height ?? "auto",
                        maxWidth: "100%",
                        maxHeight: "90vh",
                        display: "block",
                        borderRadius: 8,
                        cursor: onClick ? "pointer" : "default",
                    }}
                />
            </div>
        );
    };

    return (
        <div style={{ minHeight: "100vh" }}>
            <div style={{
                paddingTop: 10,
                paddingLeft: 20,
                paddingRight: 20,
                paddingBottom: isSetMenu ? 100 : 20,
            }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ fontSize: 32, fontWeight: "700" }}>{menuDetail?.title}</div>

                    {menuDetail?.sub_title ? (
                        <div style={{ fontSize: 16, fontWeight: "400", textAlign: "center" }}>
                            {menuDetail?.sub_title}
                        </div>
                    ) : null}

                    {isSetMenu && menuDetail?.description ? (
                        <div style={{ fontSize: 16, fontWeight: "400", paddingTop: 20, textAlign: "center" }}>
                            {menuDetail?.description}
                        </div>
                    ) : null}

                    {isSetMenu && menuDetail?.price ? (
                        <div style={{ display: "flex", flexDirection: "row", margin: "16px 0", alignItems: "center" }}>
                            <div style={{ color: "var(--bs-primary)", fontWeight: "400", fontSize: 18 }}>總價:</div>
                            <div style={{ color: "var(--bs-primary)", fontWeight: "800", fontSize: 28, marginInlineStart: 8 }}>
                                {`$${menuDetail?.price}`}
                            </div>
                        </div>
                    ) : null}
                </div>

                {shopsList.length > 0 ? (
                    <div style={{ ...getCenteredContent(), marginTop: 10 }}>
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <IoLocation color={"var(--bs-primary)"} size={12} style={{ marginRight: 10, flexShrink: 0 }} />
                            <div style={{ color: "#464A4D", wordBreak: "break-word" }}>{shopsList.join("、")}</div>
                        </div>
                    </div>
                ) : null}

                {isSetMenu && brandsList.length > 0 ? (
                    <div style={{ ...getCenteredContent(), margin: "16px auto" }}>
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <IoLocation color={"var(--bs-primary)"} size={12} style={{ marginRight: 10, flexShrink: 0 }} />
                            <div style={{ color: "#464A4D", wordBreak: "break-word" }}>{brandsList.join("、")}</div>
                        </div>
                    </div>
                ) : null}

                {(menuDetail?.photo?.data ?? []).map((img, i) => {
                    const src = toAbsoluteUrl(
                        img?.attributes?.url ||
                        img?.attributes?.formats?.large?.url ||
                        img?.attributes?.formats?.small?.url
                    );
                    if (!src) return null;

                    return (
                        <MeasuredImage
                            key={i}
                            src={src}
                            alt={`menu-${i}`}
                            onClick={() => window.open(src, "_blank")}
                        />
                    );
                })}

                {!isSetMenu ? (
                    <div style={{ padding: "20px 0", display: "flex", flexDirection: "row" }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: "700", fontSize: 16, textAlign: "center", color: "var(--bs-primary)" }}>供應品牌</div>
                            <div style={{ textAlign: "center", paddingTop: 5 }}>{shopsList.length === 0 ? "所有分店" : shopsList.join("、")}</div>
                        </div>

                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: "700", fontSize: 16, textAlign: "center", color: "var(--bs-primary)" }}>供應日期</div>
                            <div style={{ textAlign: "center", paddingTop: 5 }}>
                                {!hasDateRange
                                    ? "所有日子適用"
                                    : `${formatZhDate(menuDetail?.start_date)}至${formatZhDate(menuDetail?.expired_date)}`}
                            </div>
                        </div>
                    </div>
                ) : null}

                {!isSetMenu ? (
                    <div style={{ padding: "20px 0", display: "flex", flexDirection: "row" }}>
                        {menuDetail?.redeemed_date ? (
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: "700", fontSize: 16, textAlign: "center", color: "var(--bs-primary)" }}>供應時間</div>
                                <div style={{ textAlign: "center", paddingTop: 5 }}>{menuDetail?.redeemed_date}</div>
                            </div>
                        ) : null}
                        {menuDetail?.capacity ? (
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: "700", fontSize: 16, textAlign: "center", color: "var(--bs-primary)" }}>每席人數</div>
                                <div style={{ textAlign: "center", paddingTop: 5 }}>
                                    {menuDetail?.capacity > 0 ? `${menuDetail?.capacity}人` : "不限人數"}
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <div style={{ padding: "20px 0", display: "flex", flexDirection: "row" }}>
                    {menuDetail?.cuisines?.data ? (
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: "700", fontSize: 16, textAlign: "center", color: "var(--bs-primary)" }}>菜單</div>
                            {menuDetail?.cuisines?.data?.map((cus, i) => (
                                <div key={i} style={{ textAlign: "center", paddingTop: 5, fontSize: 16, fontWeight: "400" }}>
                                    {cus?.attributes?.name}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>

                {isSetMenu && menuDetail?.date_str ? (
                    <div style={{ ...getCenteredContent(), padding: "20px 0" }}>
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 18, fontWeight: "700", color: "var(--bs-primary)" }}>套餐日期時間說明</div>
                        </div>
                        <div style={{ fontSize: 16, fontWeight: "400", lineHeight: "24px", textAlign: "left" }}>
                            {menuDetail?.date_str}
                        </div>
                    </div>
                ) : null}

                {isSetMenu && menuDetail?.terms ? (
                    <div style={{ ...getCenteredContent(), padding: "20px 0" }}>
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 18, fontWeight: "700", color: "var(--bs-primary)" }}>條款及細則</div>
                        </div>
                        <div style={{ fontSize: 16, fontWeight: "400", lineHeight: "24px", textAlign: "left", whiteSpace: "pre-line" }}>
                            {menuDetail?.terms}
                        </div>
                    </div>
                ) : null}

                {isSetMenu ? (
                    <div style={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: "16px 20px",
                        zIndex: 100,
                    }}>
                        <button
                            type="button"
                            className="btn-primary btn"
                            style={{
                                width: "100%",
                                maxWidth: 600,
                                margin: "0 auto",
                                backgroundColor: "var(--bs-primary)",
                                padding: "16px 0",
                                alignItems: "center",
                                justifyContent: "space-around",
                                borderRadius: 50,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "row",
                                border: "none",
                                cursor: "pointer",
                            }}
                            onClick={() => {
                                navigate(`/${companyParam}/orderset`, { state: { setMenu: menuDetail, id: menuId, counting } });
                            }}
                        >
                            <div style={{ width: 24 }} />
                            <div style={{ color: "white", fontSize: 16, fontWeight: "500" }}>選擇日期和時間</div>
                            <AiOutlineArrowRight color="white" size={24} />
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}